import { supabase } from "@/integrations/supabase/client";
import { getInvokeErrorText } from "@/lib/invokeError";

/** Valor sentinela para filtrar conversas sem integração vinculada na UI. */
export const UNASSIGNED_INTEGRATION_FILTER = "__unassigned__";

export function isUnassignedIntegrationFilter(
  integrationId: string | null | undefined,
): boolean {
  return integrationId === UNASSIGNED_INTEGRATION_FILTER;
}

export type DisconnectConversationChoice =
  | { mode: "reassign"; targetIntegrationId: string }
  | { mode: "unassigned" }
  | { mode: "keep" };

export async function countConversationsForIntegration(
  workspaceId: string,
  integrationId: string,
): Promise<number> {
  const { count, error } = await supabase
    .from("conversations")
    .select("id", { count: "exact", head: true })
    .eq("workspace_id", workspaceId)
    .eq("integration_id", integrationId);

  if (error) {
    console.error("countConversationsForIntegration:", error);
    throw error;
  }
  return count ?? 0;
}

export async function countUnassignedConversationsForChannel(
  workspaceId: string,
  channel: "whatsapp" | "instagram" | "messenger",
): Promise<number> {
  const { count, error } = await supabase
    .from("conversations")
    .select("id", { count: "exact", head: true })
    .eq("workspace_id", workspaceId)
    .eq("channel", channel)
    .is("integration_id", null);

  if (error) {
    console.error("countUnassignedConversationsForChannel:", error);
    return 0;
  }
  return count ?? 0;
}

/**
 * Aplica filtro de canal + integração (ou “em aberto”) em queries do Supabase.
 *
 * `allowedIntegrationIds` (opcional) restringe a leitura às conexões que o membro
 * pode ver. Isso não adiciona consulta alguma: apenas torna o predicado da consulta
 * já existente mais seletivo, evitando trazer linhas que seriam descartadas na tela.
 */
export function applyConversationListFilters<
  Q extends {
    eq: (col: string, val: string) => Q;
    is: (col: string, val: null) => Q;
    in: (col: string, vals: string[]) => Q;
  },
>(
  query: Q,
  channel: "whatsapp" | "instagram" | "messenger",
  integrationId?: string | null,
  allowedIntegrationIds?: string[] | null,
): Q {
  let q = query.eq("channel", channel);
  if (integrationId && isUnassignedIntegrationFilter(integrationId)) {
    return q.is("integration_id", null);
  }
  if (integrationId) {
    return q.eq("integration_id", integrationId);
  }
  if (allowedIntegrationIds && allowedIntegrationIds.length > 0) {
    return q.in("integration_id", allowedIntegrationIds);
  }
  return q;
}


export function isConversationRelevantForListFilter(
  record: { channel?: string; integration_id?: string | null } | null | undefined,
  channel: "whatsapp" | "instagram" | "messenger",
  integrationId?: string | null,
): boolean {
  if (!record) return false;
  if (integrationId && isUnassignedIntegrationFilter(integrationId)) {
    return record.channel === channel && record.integration_id == null;
  }
  if (integrationId) {
    return record.integration_id === integrationId;
  }
  return record.channel === channel;
}

function providerToChannel(provider: string | null | undefined): "whatsapp" | "instagram" | "messenger" | null {
  if (provider === "whatsapp_evolution" || provider === "whatsapp_cloud" || provider === "whatsapp_uazapi") {
    return "whatsapp";
  }
  if (provider === "instagram") return "instagram";
  if (provider === "messenger") return "messenger";
  return null;
}

export async function applyDisconnectConversationChoice(
  workspaceId: string,
  sourceIntegrationId: string,
  choice: DisconnectConversationChoice,
): Promise<void> {
  if (choice.mode === "keep") return;

  if (choice.mode === "reassign") {
    const { data: source, error: sourceError } = await supabase
      .from("integrations")
      .select("provider")
      .eq("id", sourceIntegrationId)
      .maybeSingle();
    if (sourceError) {
      console.error("applyDisconnectConversationChoice:", sourceError);
      throw sourceError;
    }
    const channel = providerToChannel(source?.provider ?? null);
    if (!channel) {
      throw new Error("Canal inválido para reatribuição de conversas.");
    }
    await reassignConversationsToIntegration(workspaceId, channel, choice.targetIntegrationId, {
      includeUnassigned: false,
      sourceIntegrationIds: [sourceIntegrationId],
    });
    return;
  }

  const { error } = await supabase
    .from("conversations")
    .update({ integration_id: null })
    .eq("workspace_id", workspaceId)
    .eq("integration_id", sourceIntegrationId);

  if (error) {
    console.error("applyDisconnectConversationChoice:", error);
    throw error;
  }
}

/** Reatribui conversas órfãs (integration_id IS NULL) de um canal a uma integração. */
export async function reassignUnassignedConversations(
  workspaceId: string,
  channel: "whatsapp" | "instagram" | "messenger",
  targetIntegrationId: string,
): Promise<number> {
  const { data, error } = await supabase
    .from("conversations")
    .update({ integration_id: targetIntegrationId })
    .eq("workspace_id", workspaceId)
    .eq("channel", channel)
    .is("integration_id", null)
    .select("id");

  if (error) {
    console.error("reassignUnassignedConversations:", error);
    throw error;
  }
  return data?.length ?? 0;
}

export async function countConversationsForIntegrationIds(
  workspaceId: string,
  integrationIds: string[],
): Promise<number> {
  if (integrationIds.length === 0) return 0;
  const { count, error } = await supabase
    .from("conversations")
    .select("id", { count: "exact", head: true })
    .eq("workspace_id", workspaceId)
    .in("integration_id", integrationIds);
  if (error) {
    console.error("countConversationsForIntegrationIds:", error);
    return 0;
  }
  return count ?? 0;
}

/**
 * Reatribui conversas de um canal para uma integração-alvo, opcionalmente migrando
 * também conversas vinculadas a outras integrações (ex.: conexões legadas/desconectadas).
 */
export const REASSIGN_BATCH_SIZE = 5;
const REASSIGN_MAX_ROUNDS = 500;
const REASSIGN_MAX_IDLE_ROUNDS = 3;

export async function reassignConversationsToIntegration(
  workspaceId: string,
  channel: "whatsapp" | "instagram" | "messenger",
  targetIntegrationId: string,
  options: {
    includeUnassigned: boolean;
    sourceIntegrationIds: string[];
    batchSize?: number;
    onProgress?: (progress: { updated: number; remaining: number }) => void;
  },
): Promise<number> {
  const sources = options.sourceIntegrationIds.filter((id) => id && id !== targetIntegrationId);
  let updated = 0;
  let idleRounds = 0;

  for (let round = 0; round < REASSIGN_MAX_ROUNDS; round++) {
    const { data, error } = await supabase.functions.invoke("conversation-integration-assign", {
      body: {
        action: "reassign",
        channel,
        targetIntegrationId,
        includeUnassigned: options.includeUnassigned,
        sourceIntegrationIds: sources,
        batchSize: options.batchSize ?? REASSIGN_BATCH_SIZE,
      },
    });

    if (error) {
      console.error("reassignConversationsToIntegration:", error);
      const message = getInvokeErrorText(error) || "Não foi possível reatribuir as conversas.";
      throw new Error(message);
    }

    const payload = (data ?? {}) as { updated?: number; remaining?: number; done?: boolean };
    const roundUpdated = Number(payload.updated ?? 0);
    updated += roundUpdated;
    const remaining = Number(payload.remaining ?? 0);
    options.onProgress?.({ updated, remaining });

    if (payload.done !== false && remaining === 0) break;
    if (roundUpdated === 0) {
      idleRounds++;
      if (idleRounds >= REASSIGN_MAX_IDLE_ROUNDS) break;
    } else {
      idleRounds = 0;
    }
    // Pequena pausa entre lotes para não saturar o banco.
    await new Promise((resolve) => setTimeout(resolve, 250));
  }

  return updated;
}

